<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="container-custom">
      <!-- Back Button -->
      <button
        @click="$router.back()"
        class="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
      >
        <ArrowLeftIcon class="h-5 w-5 mr-2" />
        Voltar
      </button>
      
      <div v-if="product" class="bg-white rounded-xl shadow-sm overflow-hidden">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
          <!-- Images Section -->
          <div>
            <!-- Main Image with Zoom -->
            <div class="relative w-full max-w-md mx-auto aspect-square rounded-xl overflow-hidden bg-gray-100 mb-4 group">
              <img
                v-if="selectedImage"
                :src="getImageUrl(selectedImage)"
                :alt="product.name"
                class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-150 cursor-zoom-in"
              >
              <div v-else class="w-full h-full flex items-center justify-center">
                <PhotoIcon class="h-24 w-24 text-gray-300" />
              </div>
              <!-- Zoom Hint -->
              <div class="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                Passe o mouse para zoom
              </div>
            </div>
            
            <!-- Thumbnail Gallery -->
            <div v-if="allImages.length > 1" class="flex gap-2 overflow-x-auto pb-2 justify-center">
              <button
                v-for="(img, index) in allImages"
                :key="index"
                @click="selectedImage = img"
                class="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors"
                :class="selectedImage === img ? 'border-orange-500' : 'border-gray-200 hover:border-gray-300'"
              >
                <img :src="getImageUrl(img)" class="w-full h-full object-cover">
              </button>
            </div>
          </div>
          
          <!-- Details -->
          <div class="flex flex-col">
            <h1 class="text-3xl font-bold text-gray-900 mb-4">{{ product.name }}</h1>
            
            <p class="text-gray-600 mb-6">{{ product.description || 'Sem descrição' }}</p>
            
            <div class="flex items-baseline gap-4 mb-8">
              <span class="text-3xl font-bold text-orange-600">
                {{ formatCurrency(product.current_price || product.price) }}
              </span>
              <span v-if="product.promotional_price" class="text-xl text-gray-400 line-through">
                {{ formatCurrency(product.price) }}
              </span>
            </div>
            
            <!-- Add to Cart -->
            <div class="flex items-center gap-4 mt-auto">
              <div class="flex items-center border rounded-lg">
                <button @click="quantity > 1 && quantity--" class="p-3 hover:bg-gray-100">
                  <MinusIcon class="h-5 w-5" />
                </button>
                <span class="px-6 font-medium text-lg">{{ quantity }}</span>
                <button @click="quantity++" class="p-3 hover:bg-gray-100">
                  <PlusIcon class="h-5 w-5" />
                </button>
              </div>
              
              <button
                @click="addToCart"
                class="flex-1 px-6 py-3 rounded-lg text-white font-medium bg-orange-600 hover:bg-orange-700"
              >
                Adicionar ao Carrinho
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Loading -->
      <div v-else class="flex items-center justify-center py-20">
        <div class="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    </div>
    
    <!-- Modal de produto adicionado -->
    <AddedToCartModal
      :show="showAddedModal"
      :product-name="product?.name || ''"
      :quantity="addedQuantity"
      @close="showAddedModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { PhotoIcon, MinusIcon, PlusIcon, ArrowLeftIcon } from '@heroicons/vue/24/outline'
import { useCartStore } from '@/stores'
import { productApi } from '@/utils/api'
import { formatCurrency } from '@/utils/formatters'
import AddedToCartModal from '@/components/cart/AddedToCartModal.vue'
import type { Product } from '@/types'

const route = useRoute()
const cartStore = useCartStore()

const product = ref<Product | null>(null)
const quantity = ref(1)
const selectedImage = ref<string>('')
const showAddedModal = ref(false)
const addedQuantity = ref(1)

const allImages = computed(() => {
  if (!product.value) return []
  const images = []
  if (product.value.image_url) {
    images.push(product.value.image_url)
  }
  if (product.value.additional_images && product.value.additional_images.length > 0) {
    images.push(...product.value.additional_images)
  }
  return images
})

const getImageUrl = (url: string) => {
  if (!url) return ''
  if (url.startsWith('http')) return url
  return url
}

const addToCart = () => {
  if (!product.value) return
  
  cartStore.addItem(product.value, quantity.value)
  addedQuantity.value = quantity.value
  showAddedModal.value = true
}

const loadProduct = async () => {
  try {
    const res = await productApi.get(Number(route.params.id))
    product.value = res.data
    // Define a imagem principal como selecionada
    if (product.value?.image_url) {
      selectedImage.value = product.value.image_url
    }
  } catch (error) {
    console.error('Erro ao carregar produto:', error)
  }
}

onMounted(() => {
  loadProduct()
})
</script>
